import React, { useMemo, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import './ach300Hmi.css';

// ACH300 — the coiler, on its own PLC data.
//
// What was here before showed 13 of the 99 tags this machine records; one of its
// five pages was the literal text "SPARK TESTER", and the Manual page was a grid
// of buttons with no handlers — a control panel that controlled nothing.
//
// Every value below is read from `machineLive`, the DB0 snapshot the WebSocket
// pushes. Nothing has a default standing in for a reading: a number nobody
// measured is worse than a dash, because a dash is honest.
//
// Verified with refactor_machine_traking/scripts/_hmiBindingAudit.mjs — run it
// before adding a field. The L120 extruder modal in hmi-pages-view/ is what
// happens otherwise: eight tags copied from the L75 that this line has never
// had, and "13 rpm" printed as a literal.

const num = (v) =>
  v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v);

const fmt = (v, d = 1) => {
  const n = num(v);
  return n === null ? '—' : n.toFixed(d);
};

// ───────── instruments ─────────

const Lamp = ({ on, tone = 'ok', onLabel = 'PORNIT', offLabel = 'OPRIT' }) => (
  <span className={`ach-lamp ${on ? tone : ''}`}>{on ? onLabel : offLabel}</span>
);

const Reading = ({ label, value, unit, digits = 1, tone = '', size = '' }) => (
  <div className="ach-bay">
    <div className="ach-label">{label}</div>
    <div className={`ach-value ${tone} ${size}`}>
      {fmt(value, digits)}
      {unit ? <span className="ach-unit">{unit}</span> : null}
    </div>
  </div>
);

const State = ({ label, children }) => (
  <div className="ach-bay">
    <div className="ach-label">{label}</div>
    <div style={{ paddingTop: 4 }}>{children}</div>
  </div>
);

/** Set beside actual, because on this line the gap is the whole story. */
const SetAct = ({ label, set, act, unit, digits = 1 }) => {
  const s = num(set);
  const a = num(act);
  // Only flagged when there is something to compare against: a difference from
  // a setpoint of zero is not a deviation, it is an idle machine.
  const off = s !== null && a !== null && s !== 0 && Math.abs((a - s) / s) > 0.1;
  return (
    <div className="ach-bay">
      <div className="ach-label">{label}</div>
      <div className="ach-setact">
        <div>
          <div className="ach-label" style={{ marginBottom: 4 }}>Setat</div>
          <div className="ach-value is-set sm">
            {fmt(set, digits)}
            {unit ? <span className="ach-unit">{unit}</span> : null}
          </div>
        </div>
        <div className="ach-setact-div" />
        <div>
          <div className="ach-label" style={{ marginBottom: 4 }}>Real</div>
          <div className={`ach-value sm ${off ? 'is-bad' : ''}`}>
            {fmt(act, digits)}
            {unit ? <span className="ach-unit">{unit}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Where a mechanism sits between its own limits.
 *
 * A bare number says nothing — 10.33 means one thing between 8 and 12 and
 * another between 0 and 300 — and a traverse parked at one end is a coil being
 * wound in one place. The limits come from the PLC too, so the bar is the
 * machine's own travel rather than a range somebody assumed.
 */
const Travel = ({ label, value, min, max, unit = 'mm' }) => {
  const v = num(value);
  const lo = num(min);
  const hi = num(max);
  const pct =
    v !== null && lo !== null && hi !== null && hi !== lo
      ? Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100))
      : null;

  return (
    <div className="ach-bay">
      <div className="ach-label">{label}</div>
      <div className="ach-value">
        {fmt(value, 2)}
        <span className="ach-unit">{unit}</span>
      </div>
      <div className="ach-travel">
        {pct !== null && (
          <div className="ach-travel-mark" style={{ left: `calc(${pct}% - 3px)` }} />
        )}
      </div>
      <div className="ach-travel-ends">
        <span>{fmt(min, 1)}</span>
        {pct !== null && <span>{pct.toFixed(0)}%</span>}
        <span>{fmt(max, 1)}</span>
      </div>
    </div>
  );
};

// ───────── pages ─────────

const HomePage = ({ live }) => (
  <>
    <Row className="g-3 mb-3">
      <Col md={6}><SetAct label="Metri" set={live.METER_SET} act={live.METER_ACT} unit="m" /></Col>
      <Col md={6}>
        <SetAct label="Viteză linie" set={live.SET_SPEED} act={live.ACT_LINE_SPEED} unit="m/min" />
      </Col>
    </Row>

    <Row className="g-3 mb-3">
      <Col md={3}><Reading label="Viteză rapidă" value={live.FAST_SPEED} unit="m/min" /></Col>
      <Col md={3}><Reading label="Viteză lentă" value={live.SLOW_SPEED} unit="m/min" /></Col>
      <Col md={3}>
        <Reading label="Turație înfășurare" value={live.WINDING_ACT_RPM} unit="rot/min" digits={0} />
      </Col>
      <Col md={3}><Reading label="Cuplu HMA" value={live.HMA_TORQUE} unit="%" /></Col>
    </Row>

    <Row className="g-3">
      <Col md={3}>
        <State label="Linie">
          <Lamp on={!!live.LINE_START_RELAY} />
        </State>
      </Col>
      <Col md={3}>
        <State label="Regim viteză">
          {live.SPEED_FAST_ACTIVE ? (
            <span className="ach-lamp on">Rapid</span>
          ) : live.SPEED_SLOW_ACTIVE ? (
            <span className="ach-lamp warn">Lent</span>
          ) : (
            <span className="ach-lamp">Oprit</span>
          )}
        </State>
      </Col>
      <Col md={3}>
        <State label="Contor">
          {live.Meter_Finished ? (
            <span className="ach-lamp warn">Terminat</span>
          ) : live.Meter_Slowed ? (
            <span className="ach-lamp warn">Încetinit</span>
          ) : (
            <span className="ach-lamp on">În lucru</span>
          )}
        </State>
      </Col>
      <Col md={3}>
        <Reading label="Metri total" value={live.METER_TOTAL} unit="m" digits={0} tone="is-plain" size="sm" />
      </Col>
    </Row>
  </>
);

const TraversePage = ({ live }) => (
  <>
    <Row className="g-3 mb-3">
      <Col md={6}>
        <Travel
          label="Etalare — poziție"
          value={live.TRAVER_ACT_POS}
          min={live.TRAVERSE_INFERIOR_LIMIT}
          max={live.TRAVERSE_SUPERIOR_LIMIT}
        />
      </Col>
      <Col md={6}>
        <Travel
          label="Cărucior — poziție"
          value={live.Forverder_Act_Pos}
          min={live.Forverder_Left_Limit}
          max={live.Forverder_Right_Limit}
        />
      </Col>
    </Row>

    <Row className="g-3 mb-3">
      <Col md={3}>
        <Reading label="Diametru etalare" value={live.Travers_Diameter} unit="mm" digits={2} />
      </Col>
      <Col md={3}><Reading label="Jog etalare" value={live.Travers_Jog_Ref} digits={0} tone="is-plain" /></Col>
      <Col md={3}><Reading label="Jog cărucior" value={live.Forverder_Jog_Ref} digits={0} tone="is-plain" /></Col>
      <Col md={3}><Reading label="Jog HMA" value={live.HMA_JOQ} digits={0} tone="is-plain" /></Col>
    </Row>

    <Row className="g-3">
      <Col md={4}>
        <State label="Driver etalare">
          <Lamp on={!live.TraversDriver_Not_Ready} onLabel="Gata" offLabel="Nu e gata" />
        </State>
      </Col>
      <Col md={4}>
        <State label="Referință etalare">
          <Lamp
            on={!live.Travers_Homing_Reqired}
            onLabel="Făcută"
            offLabel="Necesară"
            tone="ok"
          />
        </State>
      </Col>
      <Col md={4}>
        <State label="Driver cărucior">
          <Lamp on={!live.Coil_Forverder_Driver_COM_Fault} onLabel="Comunică" offLabel="Fără comunicație" />
        </State>
      </Col>
    </Row>
  </>
);

/**
 * The spark tester.
 *
 * This page was the words "SPARK TESTER" and nothing else, while the PLC
 * recorded a set voltage, an actual voltage, a running fault count and fourteen
 * individual readings. The count is the number a coiler operator needs at a
 * glance: it is cable failing its insulation test.
 */
const SparkPage = ({ live }) => {
  const faults = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        n: i + 1,
        value: num(live[`SPARK_VALUE_${i + 1}`]),
      })).filter((f) => f.value !== null),
    [live]
  );
  const total = num(live.SPARK_TOTAL_PCS);

  return (
    <>
      <Row className="g-3 mb-3">
        <Col md={3}>
          <State label="Spark tester">
            <Lamp on={!!live.SPARK_ON_OFF} />
          </State>
        </Col>
        <Col md={5}>
          <SetAct label="Tensiune" set={live.SPARK_SET_KV} act={live.SPARK_ACT_KV} unit="kV" digits={2} />
        </Col>
        <Col md={4}>
          {/* Red the moment it leaves zero. One fault is one length of cable
              that failed, not a statistic to watch trend upwards. */}
          <div className={`ach-bay ${total > 0 ? 'is-alarm' : ''}`}>
            <div className="ach-label">Defecte total</div>
            <div className={`ach-value lg ${total > 0 ? 'is-bad' : 'is-plain'}`}>
              {fmt(total, 0)}
            </div>
          </div>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={12}>
          <State label="Alarmă spark">
            <Lamp on={!!live.Spark_Tester_Alarm} tone="bad" onLabel="Activă" offLabel="Inactivă" />
          </State>
        </Col>
      </Row>

      <div className="ach-label">Citiri individuale</div>
      <Row className="g-2">
        {faults.map((f) => (
          <Col key={f.n} xs={4} md={3} lg={2} xl={1}>
            <div className={`ach-cell ${f.value > 0 ? 'is-bad' : ''}`}>
              <div className="ach-cell-n">#{f.n}</div>
              <div className={`ach-value sm ${f.value > 0 ? 'is-bad' : 'is-plain'}`}>
                {f.value.toFixed(0)}
              </div>
            </div>
          </Col>
        ))}
        {!faults.length && (
          <Col xs={12}>
            <div className="ach-bay">
              <div className="ach-label">Nicio citire raportată de PLC</div>
            </div>
          </Col>
        )}
      </Row>
    </>
  );
};

/**
 * The manual mechanisms, as STATE.
 *
 * The page this replaces was eight buttons with no handlers — it looked like a
 * control panel and controlled nothing, which is worse than not having one:
 * somebody presses TĂIERE CABLU and believes the cable was cut.
 *
 * There is no write path to this PLC from the application, and inventing one
 * from a web page is not something to do quietly. So this shows what the panel
 * is asserting and whether each movement completed — which is the question an
 * operator standing at the machine actually has.
 */
const MANUAL_GROUPS = [
  {
    title: 'Cablu',
    rows: [
      ['Ținere cablu', 'Man_Cable_Hold', 'Cable_Hold_Unsuccessful'],
      ['Eliberare cablu', 'Man_Cable_Release', 'Cable_Release_Unsuccessful'],
      ['Tăiere cablu', 'Man_Cable_Cut', 'Cable_Cut_Unsuccesful'],
      ['Cablu netăiat', 'Man_Cable_Uncut', 'Cable_Uncut_Unsuccesful'],
    ],
  },
  {
    title: 'Intrare',
    rows: [
      ['Ținere cablu intrare', 'Man_Entry_CableKeep', 'Entry_Cable_Keep_Unsuccesful'],
      ['Eliberare cablu intrare', 'Man_Entry_CableRelease', 'Entry_Cable_Release_Unsuccesful'],
    ],
  },
  {
    title: 'Cap',
    rows: [
      ['Cap împingere', 'Man_Head1_Push', 'Head_Down_Unsuccessful'],
      ['Cap tragere', 'Man_Head1_Pull', 'Head_Up_Unsuccessful'],
    ],
  },
  {
    title: 'Grup ținere / tăiere',
    rows: [
      ['Grup împingere', 'Man_KeepCut_Group_Push', 'KeepCut_Group_FW_Unsuccesful'],
      ['Grup tragere', 'Man_KeepCut_Group_Pull', 'KeepCut_Group_BW_Unsuccessful'],
    ],
  },
];

const ManualPage = ({ live }) => (
  <>
    <div className="ach-note">
      Vizualizare. Aplicația nu scrie în PLC — ce vezi aici este ce comandă
      panoul mașinii, plus dacă mișcarea s-a încheiat cu succes.
    </div>

    <Row className="g-3 mb-3">
      <Col md={4}>
        <State label="Schimbare manuală">
          <Lamp on={!!live.MANUEL_CHANGE_ACTIVE} tone="warn" onLabel="Activă" offLabel="Inactivă" />
        </State>
      </Col>
      <Col md={4}>
        <State label="Resetare sistem">
          <Lamp on={!!live.System_Reset_Active} tone="warn" onLabel="În curs" offLabel="Inactivă" />
        </State>
      </Col>
      <Col md={4}>
        <State label="Cap înfășurător">
          <Lamp on={!live.Winder_Head_Not_Down} onLabel="Jos" offLabel="Nu e jos" />
        </State>
      </Col>
    </Row>

    <Row className="g-3">
      {MANUAL_GROUPS.map((g) => (
        <Col key={g.title} md={6} className="mb-1">
          <div className="ach-bay">
            <div className="ach-label">{g.title}</div>
            {g.rows.map(([label, cmdTag, failTag]) => {
              const commanded = live[cmdTag] === true;
              const failed = live[failTag] === true;
              return (
                <div
                  key={cmdTag}
                  className="d-flex justify-content-between align-items-center"
                  style={{ padding: '7px 0', borderTop: '1px solid var(--ach-line)' }}
                >
                  <span style={{ fontSize: '0.86rem' }}>{label}</span>
                  {failed ? (
                    <span className="ach-lamp bad">Eșuat</span>
                  ) : commanded ? (
                    <span className="ach-lamp on">Comandat</span>
                  ) : (
                    <span className="ach-lamp">Inactiv</span>
                  )}
                </div>
              );
            })}
          </div>
        </Col>
      ))}
    </Row>
  </>
);

/**
 * Alarms, straight off the PLC.
 *
 * Names are kept in the PLC's own spelling, typos included. The electrician
 * standing at the cabinet searches for that string, and a prettier label here
 * would break the only link between this screen and the ladder.
 */
const ALARM_TAGS = [
  'Safety_Relay_Not_OK', 'CP_EMG_Not_OK', 'PayOff_EMG_Not_OK', 'Spare_EMG_Not_OK',
  'Air_Pressure_Not_OK', 'Door_Switch_Not_Closed', 'Spark_Tester_Alarm',
  'Dancer_Limit_Sensor', 'Dancer_Motor_Fan_Fuse_Not_OK', 'Pay_Off_Not_Started',
  'Coiler_Driver_Not_Ready', 'Coiler_Driver_COM_Fault', 'Coiler_Head_Not_Start_Position',
  'Coil_Carrier_Not_Ready', 'Coil_Carrier_Homing_Required', 'Coil_Forverder_Driver_COM_Fault',
  'TraversDriver_Not_Ready', 'Travers_Driver_COM_Fault', 'Travers_Homing_Reqired',
  'Travers_Up_Limit_Fault', 'Travers_Down_Limit_Fault', 'Hma_Driver_Com_Fault',
  'Cage_Control_Cabin_Profinet_COM_Fault', 'Winder_Head_Not_Down',
  'Head_Up_Unsuccessful', 'Head_Down_Unsuccessful',
  'Cable_Cut_Unsuccesful', 'Cable_Uncut_Unsuccesful', 'Cable_Hold_Unsuccessful',
  'Cable_Release_Unsuccessful', 'Cable_Release_Not_Start_Position', 'Cable_Uncut_Not_Position',
  'Entry_Cable_Keep_Unsuccesful', 'Entry_Cable_Release_Unsuccesful', 'Entry_Cable_Not_Start_Position',
  'KeepCut_Group_FW_Unsuccesful', 'KeepCut_Group_BW_Unsuccessful', 'KeepCut_Group_Not_Start_Position',
  'ACH_Started_System_Reset_Imposible', 'WARNING_METER_FULL',
];

const activeAlarms = (live) => ALARM_TAGS.filter((t) => live[t] === true);

const AlarmsPage = ({ live }) => {
  const active = activeAlarms(live);
  const quiet = ALARM_TAGS.filter((t) => live[t] === false);
  const absent = ALARM_TAGS.filter((t) => live[t] === undefined);

  return (
    <>
      <div className={`ach-banner ${active.length ? 'bad' : 'ok'} mb-3`}>
        {active.length
          ? `${active.length} ${active.length === 1 ? 'alarmă activă' : 'alarme active'}`
          : 'Nicio alarmă activă'}
      </div>

      {active.length > 0 && (
        <Row className="g-2 mb-4">
          {active.map((t) => (
            <Col key={t} xs={12} md={6} lg={4}>
              <span className="ach-alarm">{t.replace(/_/g, ' ')}</span>
            </Col>
          ))}
        </Row>
      )}

      <div className="ach-label">
        {quiet.length} în regulă
        {absent.length ? ` · ${absent.length} nu sunt citite din PLC` : ''}
      </div>
      <Row className="g-1">
        {quiet.map((t) => (
          <Col key={t} xs={6} md={4} lg={3}>
            <div className="ach-ok-item">✓ {t.replace(/_/g, ' ')}</div>
          </Col>
        ))}
      </Row>
    </>
  );
};

// ───────── shell ─────────

const PAGES = [
  ['HOME', 'Acasă', HomePage],
  ['TRAVERSE', 'Etalare', TraversePage],
  ['SPARK', 'Spark tester', SparkPage],
  ['MANUAL', 'Manual', ManualPage],
  ['ALARMS', 'Alarme', AlarmsPage],
];

const ACH300Hmi = ({ machineLive = {} }) => {
  const [view, setView] = useState('HOME');
  const Page = (PAGES.find((p) => p[0] === view) || PAGES[0])[2];
  const alarms = activeAlarms(machineLive).length;

  return (
    <div className="ach-hmi">
      {/* The machine's own state, above the pages: which mode it believes it is
          in is the frame everything below is read in. */}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <span className="ach-label" style={{ margin: 0 }}>ACH300</span>
        <span className="ach-lamp on" style={{ opacity: machineLive.MODE ? 1 : 0.4 }}>
          {machineLive.MODE || 'fără regim'}
        </span>
        <span className="ach-lamp" style={{ opacity: machineLive.STATUS ? 1 : 0.4 }}>
          {machineLive.STATUS || 'fără stare'}
        </span>
        {machineLive.SHIFT ? (
          <span className="ach-label" style={{ margin: 0 }}>Schimb {machineLive.SHIFT}</span>
        ) : null}
      </div>

      <div className="ach-nav">
        {PAGES.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`ach-tab ${view === key ? 'is-active' : ''}`}
            onClick={() => setView(key)}
          >
            {label}
            {key === 'ALARMS' && alarms > 0 ? (
              <span className="ach-tab-badge">{alarms}</span>
            ) : null}
          </button>
        ))}
      </div>

      <Page live={machineLive} />
    </div>
  );
};

export default ACH300Hmi;
